import React, { useEffect, useRef } from 'react'
import Text from '@embracesbs/component-text'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon'

interface SessionNoticeProps {
  title: string
  message: string
  actionText: string
  onAction: () => void
}

// Shown when re-authentication could not be completed on its own. Previously
// this case only reached the console, so the app just went quiet: every API
// call short-circuits on the missing token and pages render empty.
//
// AuthProvider renders this *over* a still-mounted app (see AuthContext), so
// the router and every open form survive a recoverable token failure. That
// makes it a modal in practice, hence alertdialog + moving focus in: the app
// behind would otherwise keep the keyboard and the screen reader. The shell
// lives in index.css (.tma-auth-overlay/.tma-auth-card) next to the other
// app-shell classes, which keeps the dark and accessibility themes working
// without a second copy of AppLoading's pre-theme inline styles.
const SessionNotice: React.FC<SessionNoticeProps> = ({ title, message, actionText, onAction }) => {
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    cardRef.current?.focus()
  }, [])

  return (
    <div className='tma-auth-overlay' role='alertdialog' aria-modal='true' aria-label={title}>
      <div className='tma-auth-card' ref={cardRef} tabIndex={-1}>
        {/* Decorative: the heading below already says what happened. */}
        <span aria-hidden='true'>
          <Icon iconName='Lock1' size={24} color='var(--tma-text-muted)' />
        </span>
        <Text textStyle='h6' content={title} />
        <Text textStyle='description' textColor='grey' content={message} />
        <div style={{ marginTop: '18px' }}>
          <Button2 text={actionText} variant='contained' buttonRadius='8px' buttonSize='40px' onClick={onAction} />
        </div>
      </div>
    </div>
  )
}

export default SessionNotice
